import { defaultPageSizeOptions, GridViewMetadata } from '../../../grid/gridView/types';
import { Column, ColumnKeys, Dependencies, SchedulerGridSchema, SubScheduleGridEvents } from '../types';
import { Filter } from '../../../grid/gridFilters/types';
import { ColumnDetails, ColumnType } from '../../../grid/types/common';
import { WebAccessControlSchedulerFormGridDataProvider } from '../dataProvider/WebAccessControlSchedulerFormGridDataProvider';
import { EnumValues, getWebCategoriesLabels } from '../../webAccessControlSchedulerApp/types';
import { html } from 'lit';
import { padHoursAndMinutes } from '../../../utils/padHoursAndMinutes.utils';
import { Rights } from '@nirvana/shared/constants';
import { translateFn } from '../../../utils/translator.helper';

export class GridConfig {
  private filtersUiConfig = [];
  public getDataForView(dependencies: Dependencies): SchedulerGridSchema {
    const viewMetaData: GridViewMetadata<Column, ColumnKeys> = {
      viewId: 'sub-schedule-grid',
      filters: this.getDefaultFilters(),
      predefinedFiltersKeys: this.getPredefinedFiltersKeys(),
      columns: this.getColumns(),
      pageSize: 100,
      isCompactViewChecked: true,
      hideHeaderActions: true,
      hideFiltersToggle: true,
      removeFilters: true,
      hideFooter: true,
      hasSelectableRows: false,
      showGlobalActions: false,
      enableCSVExport: false,
      pageSizeOptions: defaultPageSizeOptions,
      columnRenderers: this.getColumnRenderers(dependencies),
      columnConfigs: [],
      allColumnDetails: this.getColumnDetails(),
      pageNumber: 1,
      filtersUiConfigs: this.filtersUiConfig,
      dataProvider: new WebAccessControlSchedulerFormGridDataProvider(dependencies),
    };

    return {
      viewMetaData,
    };
  }

  private getColumnRenderers(dependencies: Dependencies) {
    const { isFormEnabled }: Dependencies = dependencies;
    const subschedules = dependencies?.schedule?.subschedules ?? [];
    const disabled = !Application.Main.hasAllRights([Rights.MANAGE_POLICIES_WRITE]);

    return {
      [ColumnKeys.SCHEDULER_NAME]: rowData => {
        return html` <gz-grid-cell-default-renderer .label="${rowData.item.name}"></gz-grid-cell-default-renderer>`;
      },

      [ColumnKeys.PRIORITY]: rowData => {
        return html` <gz-grid-cell-default-renderer .label="${rowData.index+1}"></gz-grid-cell-default-renderer>`;
      },

      [ColumnKeys.CATEGORIES]: rowData => {
        const subscheduleCategories: Array<number> = Array.isArray(rowData.item.categories) ? rowData.item.categories : [];
        const categoriesAffected: Array<string> = getWebCategoriesLabels(subscheduleCategories);
        categoriesAffected.sort();
        return html` <gz-grid-cell-default-renderer .label="${categoriesAffected.join(", ")}"></gz-grid-cell-default-renderer>`;
      },

      [ColumnKeys.TIME_RANGE]: rowData => {
        const startTime = padHoursAndMinutes`${rowData.item?.timeInterval?.startHour}:${rowData.item?.timeInterval?.startMinute}`;
        const endTime = padHoursAndMinutes`${rowData.item?.timeInterval?.endHour}:${rowData.item?.timeInterval?.endMinute}`;
        const label = `${startTime} - ${endTime}`;
        return html` <gz-grid-cell-default-renderer .label="${label}"></gz-grid-cell-default-renderer>`;
      },

      [ColumnKeys.ACTIONS]: rowData => {
        const cssClassForUpButton = rowData.index === 0 ? "hidden" : "";
        const cssClassForDown = rowData.index === subschedules.length -1 ? "hidden" : "";
        return html`
          <gz-icon-button ?disabled="${disabled || !isFormEnabled}" icon="ds-icon-edit_pen" @click="${this.onEditClick.bind(this, rowData)}" buttoncolor="secondary" fontsize="20" buttontype="round" buttonfill="quiet" size="24"></gz-icon-button>
          <gz-icon-button ?disabled="${disabled || !isFormEnabled}" icon="ds-icon-clear" @click="${this.onDeleteClick.bind(this, rowData)}" buttoncolor="secondary" fontsize="20" buttontype="round" buttonfill="quiet" size="24"></gz-icon-button>
          <gz-icon-button ?disabled="${disabled || !isFormEnabled}" class="${cssClassForUpButton}" icon="ds-icon-arrow_up_collapse" @click="${this.onUpClick.bind(this, rowData)}" buttoncolor="secondary" fontsize="20" buttontype="round" buttonfill="quiet" size="24"></gz-icon-button>
          <gz-icon-button ?disabled="${disabled || !isFormEnabled}" class="${cssClassForDown}" icon="ds-icon-arrow_down_expand" @click="${this.onDownClick.bind(this, rowData)}"  buttoncolor="secondary" fontsize="20" buttontype="round" buttonfill="quiet" size="24"></gz-icon-button>
        `;
      },

    };
  }

  private getDefaultFilters(): Array<Filter<ColumnKeys>> {
    return [];
  }

  private getPredefinedFiltersKeys(): Array<ColumnKeys> {
    return [];
  }

  private getColumns(): Column[] {
    return [
      Column.PRIORITY,
      Column.SCHEDULER_NAME,
      Column.CATEGORIES,
      Column.TIME_RANGE,
      Column.ACTIONS,
    ];
  }

  private getColumnDetails(): Array<ColumnDetails<Column, ColumnKeys>> {
    return [
      {
        column: Column.PRIORITY,
        key: ColumnKeys.PRIORITY,
        columnName: translateFn('Priority'),
        type: ColumnType.STRING,
        removable: false,
        sortable: false,
        width: '100px',
      },
      {
        column: Column.SCHEDULER_NAME,
        key: ColumnKeys.SCHEDULER_NAME,
        columnName: translateFn('Name'),
        type: ColumnType.STRING,
        removable: false,
        sortable: false,
      },
      {
        column: Column.CATEGORIES,
        key: ColumnKeys.CATEGORIES,
        columnName: translateFn('Categories'),
        type: ColumnType.STRING,
        removable: false,
        sortable: false,
      },
      {
        column: Column.TIME_RANGE,
        key: ColumnKeys.TIME_RANGE,
        columnName: translateFn('Time Range'),
        type: ColumnType.STRING,
        removable: false,
        sortable: false,
      },
      {
        column: Column.ACTIONS,
        key: ColumnKeys.ACTIONS,
        columnName: translateFn('Actions'),
        type: ColumnType.ACTION,
        removable: false,
        sortable: false,
      },
    ];
  }

  public getEnumValues(keyValueMap: Record<number, string>): EnumValues[] {
    const labels: EnumValues[] = [];
    for (const [value, label] of Object.entries(keyValueMap)) {
      labels.push({
        label: label,
        value: Number(value),
      });
    }
    return labels;
  }

  private onUpClick(rowData, event: CustomEvent) {
    (event.target as HTMLTextAreaElement)?.getRootNode()?.dispatchEvent(
      new CustomEvent(SubScheduleGridEvents.CLICK_ON_UP_ICON, {
        detail: {
          rowData: rowData,
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  private onDownClick(rowData, event: CustomEvent) {
    (event.target as HTMLTextAreaElement)?.getRootNode()?.dispatchEvent(
      new CustomEvent(SubScheduleGridEvents.CLICK_ON_DOWN_ICON, {
        detail: {
          rowData: rowData,
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  private onEditClick(rowData, event: CustomEvent) {
    (event.target as HTMLTextAreaElement)?.getRootNode()?.dispatchEvent(
      new CustomEvent(SubScheduleGridEvents.CLICK_ON_EDIT_ICON, {
        detail: {
          rowData: rowData,
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  private onDeleteClick(rowData, event: CustomEvent) {
    (event.target as HTMLTextAreaElement)?.getRootNode()?.dispatchEvent(
      new CustomEvent(SubScheduleGridEvents.CLICK_ON_DELETE_ICON, {
        detail: {
          rowData: rowData,
        },
        bubbles: true,
        composed: true,
      })
    );
  }

}
